import { Component, HostListener, forwardRef, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

@Component({
  selector: 'rr-select-btn',
  standalone: false,

  templateUrl: './select-btn.component.html',
  styleUrl: './select-btn.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectBtnComponent),
      multi: true,
    }
  ]
})
export class SelectBtnComponent implements OnDestroy {
  @Input() options: Array<any> = [];
  @Input() label: string = '';
  control: FormControl = new FormControl();
  @Input() placeholder: string = '';
  @Input() optionName: string = '';
  @Input() optionValue: string = '';

  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() preselectedValue: any = null;

  selectedName: string = '';
  selectedIcon: string = '';
  showOptions: boolean = false;
  elementWidth: string = 'auto';
  errorClass: string = '';
  focusedOptionIndex: number = -1;

  constructor() { }

  ngOnInit() {
    this.initializeSelectedOption();
    this.control.valueChanges.subscribe((value) => {
      this.updateSelectedOption(value);
    });
    if (this.preselectedValue) {
      setTimeout(() => {
        this.updateSelectedOption(this.preselectedValue);
      }, 50);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.initializeSelectedOption();
    }
    if (this.required == true) {
      this.control.setValidators(Validators.required);
    }
  }

  initializeSelectedOption() {
    const selectedOption = this.options?.find(option => option[this.optionValue] === this.control.value);
    if (selectedOption) {
      this.selectedName = selectedOption[this.optionName];
      this.selectedIcon = selectedOption.icon || '';
    }
  }

  updateSelectedOption(value: any) {
    const selectedOption = this.options.find(option => option[this.optionValue] === value);
    if (selectedOption) {
      this.selectedName = selectedOption[this.optionName];
      this.selectedIcon = selectedOption.icon || '';
    }

  }

  selectOption(option: any) {
    if (option === undefined) {
      this.selectedName = '';
      this.selectedIcon = '';
      this.control.setValue(null);
      this.closeOptions();
      return;
    }

    this.selectedName = option[this.optionName];
    this.selectedIcon = option.icon || '';
    this.closeOptions();
    this.control.setValue(option[this.optionValue]);
    this.onChange(option[this.optionValue]);
    this.onTouched();
    this.checkValidation();
  }

  toggleOptions(eventTarget: any) {
    if (this.disabled == true) {
      return;
    }

    if (this.showOptions) {
      this.closeOptions();
      return;
    }

    this.showOptions = !this.showOptions;
    this.lockBodyScroll(true);

    const selectedIndex = this.options.findIndex(option => option[this.optionValue] === this.control.value);
    this.focusedOptionIndex = selectedIndex >= 0 ? selectedIndex : (this.options.length ? 0 : -1);

    if (this.showOptions) {
      setTimeout(() => {
        const element = (eventTarget as HTMLElement).closest('.mainselect');
        if (element) {
          const width = element.clientWidth;
          this.elementWidth = width + 2 + 'px';
        } else {
          this.elementWidth = 'auto';
        }
      }, 0);
    }
  }

  closeOptions() {
    this.showOptions = false;
    this.focusedOptionIndex = -1;
    this.lockBodyScroll(false);
  }

  onBackdropClick() {
    this.checkValidation();
    this.closeOptions();
  }

  private lockBodyScroll(lock: boolean) {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    if (!this.showOptions || this.disabled) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeOptions();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.options.length) {
        return;
      }
      this.focusedOptionIndex = (this.focusedOptionIndex + 1 + this.options.length) % this.options.length;
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.options.length) {
        return;
      }
      this.focusedOptionIndex = (this.focusedOptionIndex - 1 + this.options.length) % this.options.length;
      return;
    }

    if (event.key === 'Enter' && this.focusedOptionIndex >= 0) {
      event.preventDefault();
      this.selectOption(this.options[this.focusedOptionIndex]);
    }
  }

  checkValidation() {
    if (this.required == true) {
      if (this.control.value == null || this.control.value == '') {
        this.errorClass = 'error';
        this.control.setErrors({ required: true });
        this.control.markAsTouched();
      } else {
        this.errorClass = '';
        this.control.setErrors(null);
      }
    }
  }

  writeValue(value: any): void {
    this.control.setValue(value);
  }

  onChange: any = () => { this.checkValidation(); };
  onTouched: any = () => {
    this.checkValidation();
  };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.lockBodyScroll(false);
  }
}
